import React, { useEffect, useMemo, useState } from 'react';
import {
	ApButtonIcon,
	ApButtonMain,
	ApButtonSecondary,
} from '@alixpartners/ui-components';
import MultiSelect from '../components/MultiSelect/MultiSelect';
import {
	ActionWrapper,
	Description,
	Divider,
	FormItemWrapper,
	FormWrapper,
	Title,
	Wrapper,
} from './CreateKey.styles';
import { uniqueId } from 'lodash';
import { getTimelineSettings, saveTimelineSettings } from 'utils/apiRequests';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { NotificationTypes } from 'components/Toast/constants';
import { keyToSaveAndRetriveSKUSetting } from '../../../utils';
import { Loader } from 'SpeedToMarket/Tabs/CategoryWidget/utils';

const CreateKey = ({ customColumns, onBackHandler }) => {
	const { showToast } = useToastMessageContext();
	const [rows, setRows] = useState([
		{ id: uniqueId(), selected: [], label: '' },
	]);
	const [isKeySavingOrFetching, setIsKeySavingOrFetching] = useState(true);
	const [defaultOptions] = useState([
		...customColumns?.filter(x => x.isActive).map(x => {
			return {
				label: x.description === '' ? x.columnName : x.description,
				value: x.columnName,
			};
		}),
		...[
			{ label: 'Origin Country', value: 'origin_country' },
			{ label: 'Destination Country', value: 'Destination_Country' },
		],
	]);

	useEffect(
		() => {
			const fetchKeys = async () => {
				const bodyParam = {
					key: keyToSaveAndRetriveSKUSetting,
				};
				const { succeeded, data } = await getTimelineSettings(bodyParam);
				if (succeeded) {
					const timelineKeys = data.filter(
						x => x.key === keyToSaveAndRetriveSKUSetting
					);
					if (timelineKeys.length > 0) {
						const keysPresent = JSON.parse(timelineKeys[0].value).map(x => {
							return { id: uniqueId(), selected: x.selected, label: x.label };
						});
						setRows(keysPresent);
					}
					setIsKeySavingOrFetching(false);
				} else {
					setIsKeySavingOrFetching(false);
				}
			};
			fetchKeys();
		}, // eslint-disable-next-line
		[]
	);

	const onAddRowHandler = () => {
		setRows([...rows, { id: uniqueId(), selected: [], label: '' }]);
	};

	const onRemoveRowHandler = id => {
		let newRows = [...rows];
		newRows.splice(id, 1);
		setRows(newRows);
	};

	const onValueSelectionHandler = (id, value) => {
		let newRows = [...rows];
		newRows[id].selected = value;
		newRows[id].label = value
			.map(x => defaultOptions.filter(y => y.value === x)[0].label)
			.join(' AND ');
		setRows(newRows);
	};

	const onKeySaveHandler = () => {
		const newArray = rows.map(x => {
			return {
				label: x.label,
				selected: x.selected,
			};
		});
		const bodyParam = {
			key: keyToSaveAndRetriveSKUSetting,
			value: JSON.stringify(newArray),
		};
		setIsKeySavingOrFetching(true);
		saveTimelineSettings(bodyParam).then(response => {
			if (response.succeeded) {
				showToast('Keys saved successfully!', NotificationTypes.success, 2000);
			} else {
				showToast('Failed to save the keys.', NotificationTypes.danger, 2000);
			}
			setIsKeySavingOrFetching(false);
		});
	};

	const isAnyKeyCreated = useMemo(
		() => rows.filter(x => x.selected.length > 0).length > 0,
		[rows]
	);

	return (
		<Wrapper>
			<Title>Generate Keys</Title>
			<Description>
				Add or remove keys. Select columns in dropdown to generate key.
			</Description>
			<Divider />
			{isKeySavingOrFetching ? (
				<Loader />
			) : (
				<>
					<FormWrapper>
						{rows.length > 0 &&
							rows.map((x, index) => {
								return (
									<FormItemWrapper key={x.id}>
										<div>Key {index + 1}: </div>
										<MultiSelect
											name="Options"
											label="Select Options"
											options={defaultOptions}
											selected={x.selected}
											onChange={e => onValueSelectionHandler(index, e)}
										/>
										<ApButtonIcon
											className="remove_icon"
											title="Remove key"
											iconProps={{ iconName: 'remove', material: true }}
											onClick={() => onRemoveRowHandler(index)}
										/>
										{x.selected.length > 0 && (
											<Description>{x.label}</Description>
										)}
									</FormItemWrapper>
								);
							})}
					</FormWrapper>
					<ActionWrapper>
						<ApButtonSecondary
							iconName="baseline_add"
							onClick={onAddRowHandler}
						>
							Add Row
						</ApButtonSecondary>
						<div style={{ display: 'flex', gap: '10px' }}>
							<ApButtonSecondary onClick={onBackHandler}>
								Back
							</ApButtonSecondary>
							<ApButtonMain
								disabled={!isAnyKeyCreated}
								onClick={onKeySaveHandler}
							>
								Save
							</ApButtonMain>
						</div>
					</ActionWrapper>
				</>
			)}
		</Wrapper>
	);
};

export default CreateKey;
